import React, { useState } from 'react';
import { login, register } from '../services/pocketbase';
import { FiX, FiMail, FiLock, FiUser, FiArrowRight } from 'react-icons/fi';
import './AuthModal.css';

function AuthModal({ isOpen, onClose, onLoginSuccess }) {
    const [activeTab, setActiveTab] = useState('login'); // 'login' or 'register'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // États du formulaire
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        passwordConfirm: ''
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (activeTab === 'login') {
                // Connexion
                await login(formData.email, formData.password);
                setLoading(false);
                onLoginSuccess();
                onClose();
            } else {
                // Inscription
                if (formData.password !== formData.passwordConfirm) {
                    throw new Error("Les mots de passe ne correspondent pas.");
                }

                // Créer l'utilisateur
                await register({
                    email: formData.email,
                    password: formData.password,
                    passwordConfirm: formData.passwordConfirm,
                    name: formData.name,
                });

                // Connecter automatiquement après inscription
                await login(formData.email, formData.password);

                setLoading(false);
                onLoginSuccess();
                onClose();
            }
        } catch (err) {
            console.error(err);
            setLoading(false);
            // Messages d'erreur simplifiés pour l'utilisateur
            if (err.message.includes('Failed to authenticate')) {
                setError("Email ou mot de passe incorrect.");
            } else if (err.data?.data?.email?.message) {
                setError(`Email: ${err.data.data.email.message}`);
            } else if (err.data?.data?.password?.message) {
                setError(`Mot de passe: ${err.data.data.password.message}`);
            } else {
                setError(err.message || "Une erreur est survenue.");
            }
        }
    };

    const switchTab = (tab) => {
        setActiveTab(tab);
        setError(null);
    };

    return (
        <div className="auth-modal-overlay" onClick={onClose}>
            <div className="auth-modal-container" onClick={e => e.stopPropagation()}>
                <button className="auth-modal-close" onClick={onClose} aria-label="Fermer">
                    <FiX />
                </button>

                <div className="auth-modal-header">
                    <h2>{activeTab === 'login' ? 'Bon retour ! ✨' : 'Bienvenue ! 🌸'}</h2>
                    <p>
                        {activeTab === 'login'
                            ? 'Connectez-vous pour gérer vos rendez-vous.'
                            : 'Créez un compte pour prendre rendez-vous plus facilement.'}
                    </p>
                </div>

                <div className="auth-modal-tabs">
                    <button
                        className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                        onClick={() => switchTab('login')}
                    >
                        Connexion
                    </button>
                    <button
                        className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
                        onClick={() => switchTab('register')}
                    >
                        Inscription
                    </button>
                </div>

                {error && <div className="auth-error-message">{error}</div>}

                <form className="auth-form" onSubmit={handleSubmit}>
                    {activeTab === 'register' && (
                        <div className="auth-input-group">
                            <div className="auth-input-icon"><FiUser /></div>
                            <input
                                type="text"
                                name="name"
                                placeholder="Votre nom complet"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    )}

                    <div className="auth-input-group">
                        <div className="auth-input-icon"><FiMail /></div>
                        <input
                            type="email"
                            name="email"
                            placeholder="Votre adresse email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="auth-input-group">
                        <div className="auth-input-icon"><FiLock /></div>
                        <input
                            type="password"
                            name="password"
                            placeholder="Votre mot de passe"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            minLength={8}
                        />
                    </div>

                    {activeTab === 'register' && (
                        <div className="auth-input-group">
                            <div className="auth-input-icon"><FiLock /></div>
                            <input
                                type="password"
                                name="passwordConfirm"
                                placeholder="Confirmez le mot de passe"
                                value={formData.passwordConfirm}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    )}

                    <button type="submit" className="auth-submit-btn" disabled={loading}>
                        {loading ? <span className="auth-spinner"></span> : (
                            <>
                                {activeTab === 'login' ? 'Se connecter' : 'S\'inscrire'}
                                <FiArrowRight style={{ marginLeft: '8px' }} />
                            </>
                        )}
                    </button>
                </form>

                <div className="auth-footer">
                    {activeTab === 'login' ? (
                        <p>
                            Pas encore de compte ?{' '}
                            <button className="auth-link-btn" onClick={() => switchTab('register')}>
                                Créer un compte
                            </button>
                        </p>
                    ) : (
                        <p>
                            Déjà un compte ?{' '}
                            <button className="auth-link-btn" onClick={() => switchTab('login')}>
                                Se connecter
                            </button>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AuthModal;
